import { StripeService } from '../services/stripe';
import type { PartnerSubscription } from '../types';

export class SubscriptionManager {
  private container: HTMLElement;
  private partnerId: string;
  private partnerEmail: string;
  private businessName: string;
  private subscription: PartnerSubscription | null = null;
  private onSubscriptionChange?: (isActive: boolean) => void;

  constructor(
    container: HTMLElement, 
    partnerId: string, 
    partnerEmail: string, 
    businessName: string,
    onSubscriptionChange?: (isActive: boolean) => void
  ) {
    this.container = container;
    this.partnerId = partnerId;
    this.partnerEmail = partnerEmail;
    this.businessName = businessName;
    this.onSubscriptionChange = onSubscriptionChange;
    this.loadSubscription();
    
    // Listen for page visibility changes to refresh subscription status
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) {
        // Page became visible, refresh subscription status
        this.loadSubscription();
      }
    });
    
    // Listen for focus events to refresh when user returns to tab
    window.addEventListener('focus', () => {
      this.loadSubscription();
    });
  }

  private async loadSubscription(): Promise<void> {
    try {
      // First try to sync with Stripe to get the most current data
      let result = await StripeService.syncAndGetSubscriptionStatus(this.partnerId);
      
      // If sync fails, fall back to database data
      if (!result.success) {
        console.warn('Stripe sync failed, using database data:', result.error);
        result = await StripeService.getSubscriptionStatus(this.partnerId);
      }
      
      if (result.success) {
        this.subscription = result.subscription || null;
        this.render();
      } else {
        this.showError(result.error || 'Failed to load subscription');
      }
    } catch (error) {
      console.error('Error loading subscription:', error);
      this.showError('Failed to load subscription status');
    }
  }

  private render(): void {
    const isActive = StripeService.isSubscriptionActive(this.subscription);
    const statusText = StripeService.getSubscriptionStatusText(this.subscription);

    this.container.innerHTML = `
      <div class="subscription-card">
        <div class="subscription-header">
          <h3>💳 Subscription Status</h3>
          <span class="status-badge ${isActive ? 'active' : 'inactive'}">${statusText}</span>
        </div>
        
        ${this.renderSubscriptionContent(isActive)}
      </div>
    `;

    this.attachEventListeners();
    
    // Notify parent component about subscription state
    if (this.onSubscriptionChange) {
      this.onSubscriptionChange(isActive);
    }
  }

  private renderSubscriptionContent(isActive: boolean): string {
    const statusText = StripeService.getSubscriptionStatusText(this.subscription);
    
    if (isActive && this.subscription) {
      return `
        <div class="subscription-active">
          <p class="subscription-info">
            ✅ Your partner subscription is active and gives you full access to all portal features.
          </p>
          <div class="subscription-details">
            <p><strong>📅 Current period:</strong> ${this.formatDate(this.subscription.current_period_start)} - ${this.formatDate(this.subscription.current_period_end)}</p>
            <p><strong>📊 Status:</strong> ${statusText}</p>
            <p><strong>🔗 Subscription ID:</strong> ${this.subscription.stripe_subscription_id || 'N/A'}</p>
          </div>
          <div class="subscription-actions">
            <button class="btn-secondary" id="manage-subscription">
              ⚙️ Manage Subscription
            </button>
          </div>
        </div>
      `;
    } else if (this.subscription && this.subscription.subscription_status === 'past_due') {
      return `
        <div class="subscription-warning">
          <p class="warning-message">
            ⚠️ Your subscription payment is past due. Please update your payment method to continue accessing the portal.
          </p>
          <div class="subscription-actions">
            <button class="btn-primary" id="manage-subscription">
              💳 Update Payment Method
            </button>
          </div>
        </div>
      `;
    } else {
      return `
        <div class="subscription-inactive">
          <p class="subscription-info">
            💎 Subscribe to unlock powerful partner features including treasure management, analytics, and customer engagement tools.
          </p>
          <div class="pricing-info">
            <div class="price">$29<span class="period">/month</span></div>
            <ul class="features">
              <li>🎯 Create and manage treasures</li>
              <li>📊 Customer analytics dashboard</li>
              <li>🎉 Marketing tools and promotions</li>
              <li>🏆 Priority customer support</li>
            </ul>
          </div>
          <div class="subscription-actions">
            <button class="btn-primary" id="start-subscription">
              🚀 Start Subscription
            </button>
          </div>
        </div>
      `;
    }
  }

  private attachEventListeners(): void {
    const startBtn = this.container.querySelector('#start-subscription');
    const manageBtn = this.container.querySelector('#manage-subscription');

    if (startBtn) {
      startBtn.addEventListener('click', () => this.startSubscription());
    }

    if (manageBtn) {
      manageBtn.addEventListener('click', () => this.manageSubscription());
    }
  }

  private async startSubscription(): Promise<void> {
    try {
      // First check if user already has an active subscription
      await this.loadSubscription();
      
      if (StripeService.isSubscriptionActive(this.subscription)) {
        // Instead of showing error, just refresh the UI to show the correct state
        this.render();
        return;
      }

      this.showLoading('Creating subscription...');
      
      const result = await StripeService.createSubscription(
        this.partnerId, 
        this.partnerEmail, 
        this.businessName
      );

      if (result.success && result.checkout_url) {
        // Redirect to Stripe Checkout
        window.location.href = result.checkout_url;
      } else {
        this.showError(result.error || 'Failed to create subscription');
      }
    } catch (error) {
      console.error('Error starting subscription:', error);
      this.showError('Failed to start subscription');
    }
  }

  private async manageSubscription(): Promise<void> {
    try {
      this.showLoading('Opening customer portal...');
      
      const result = await StripeService.createCustomerPortalSession(this.partnerId);

      if (result.success && result.portal_url) {
        // Redirect to Stripe Customer Portal
        window.location.href = result.portal_url;
      } else {
        this.showError(result.error || 'Failed to open customer portal');
      }
    } catch (error) {
      console.error('Error managing subscription:', error);
      this.showError('Failed to open customer portal');
    }
  }

  private showLoading(message: string): void {
    this.container.innerHTML = `
      <div class="loading-state">
        <div class="spinner"></div>
        <p>${message}</p>
      </div>
    `;
  }

  private showError(error: string): void {
    this.container.innerHTML = `
      <div class="error-state">
        <p class="error-message">❌ ${error}</p>
        <button class="btn-secondary" onclick="location.reload()">Retry</button>
      </div>
    `;
  }

  private formatDate(dateString: string | undefined): string {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  }

  // Public method to refresh subscription status
  public async refresh(): Promise<void> {
    await this.loadSubscription();
  }
}
